import React, { useState, useEffect } from 'react';
import {
    FiUsers, FiMapPin, FiBook, FiCalendar, FiSearch,
    FiFilter, FiRefreshCw, FiClock, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import api from '../../utils/api';
import { formatIndianDate } from '../../utils/dateUtils';

const font = "'Outfit', sans-serif";
const C = {
    text: '#0f172a', sub: '#64748b', border: '#bae6fd',
    bg: '#f0f9ff', white: '#fff', accent: '#0ea5e9',
    green: '#059669', orange: '#d97706', red: '#dc2626'
};

const statusColors = {
    scheduled: { bg: '#fef3c7', c: '#b45309', label: 'Scheduled' },
    active:    { bg: '#d1fae5', c: '#059669', label: '🔴 LIVE' },
    ended:     { bg: '#f1f5f9', c: '#64748b', label: 'Ended' },
};

const SessionBadge = ({ status }) => {
    const conf = statusColors[status] || statusColors.scheduled;
    return (
        <span style={{
            background: conf.bg, color: conf.c,
            padding: '3px 10px', borderRadius: 20,
            fontSize: 10, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: 0.5,
            whiteSpace: 'nowrap'
        }}>{conf.label}</span>
    );
};

const InvigilatorManage = () => {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterDate, setFilterDate] = useState('');
    const [expandedInvig, setExpandedInvig] = useState(null);
    const [groupBy, setGroupBy] = useState('invigilator'); // 'invigilator' | 'session' | 'hall'

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStatus) params.append('status', filterStatus);
            if (filterDate) params.append('date', filterDate);
            const res = await api.get(`/api/exam-controller/invigilator-overview?${params.toString()}`);
            setAssignments(res.data.assignments || []);
        } catch (err) {
            console.error(err);
            setAssignments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filterStatus, filterDate]);

    // ── Stats ─────────────────────────────────────────────────────────────────
    const totalInvigilators = new Set(
        assignments.flatMap(a => (a.invigilator_list || []).map(i => i.username))
    ).size;
    const liveAssignments = assignments.filter(a => a.session_status === 'active').length;
    const unassigned = assignments.filter(a => !a.invigilator_username || a.invigilator_list?.length === 0).length;

    // ── Search filter ─────────────────────────────────────────────────────────
    const q = search.toLowerCase();
    const filtered = assignments.filter(a =>
        !q ||
        (a.invigilator_names || '').toLowerCase().includes(q) ||
        (a.hall_number || '').toLowerCase().includes(q) ||
        (a.subject_name || '').toLowerCase().includes(q) ||
        (a.subject_code || '').toLowerCase().includes(q) ||
        (a.department || '').toLowerCase().includes(q) ||
        (a.batch_name || '').toLowerCase().includes(q)
    );

    // ── Group by invigilator ──────────────────────────────────────────────────
    const groupedByInvig = {};
    filtered.forEach(a => {
        const invigs = a.invigilator_list?.length > 0
            ? a.invigilator_list
            : [{ username: '__unassigned__', display_name: 'Unassigned', name: 'Unassigned', department: '', designation: '' }];
        invigs.forEach(inv => {
            const key = inv.display_name || inv.name || inv.username;
            if (!groupedByInvig[key]) groupedByInvig[key] = {
                display_name: key,
                username: inv.username,
                department: inv.department || '',
                designation: inv.designation || '',
                rows: []
            };
            groupedByInvig[key].rows.push(a);
        });
    });

    // ── Group by session ──────────────────────────────────────────────────────
    const groupedBySession = {};
    filtered.forEach(a => {
        const key = `${a.subject_name} | ${a.exam_date?.slice(0, 10)}`;
        if (!groupedBySession[key]) groupedBySession[key] = { label: key, session: a, rows: [] };
        groupedBySession[key].rows.push(a);
    });

    return (
        <div style={{ fontFamily: font }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiUsers color={C.accent} /> Invigilator Management
                </h1>
                <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>
                    View which invigilator is assigned to which hall and exam session.
                </p>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
                {[
                    { label: 'Total Assignments', val: filtered.length, icon: <FiMapPin />, c: C.accent, bg: '#e0f2fe' },
                    { label: 'Unique Invigilators', val: totalInvigilators, icon: <FiUsers />, c: C.green, bg: '#d1fae5' },
                    { label: 'Live Now', val: liveAssignments, icon: <FiClock />, c: '#dc2626', bg: '#fee2e2' },
                    { label: 'Unassigned Halls', val: unassigned, icon: <FiMapPin />, c: C.orange, bg: '#fef3c7' },
                ].map((s, i) => (
                    <div key={i} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: s.bg, color: s.c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>{s.val}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Search */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`, padding: '8px 12px' }}>
                    <FiSearch size={14} color={C.sub} />
                    <input
                        type="text"
                        placeholder="Search by name, hall, subject, dept..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, fontFamily: font, flex: 1, color: C.text }}
                    />
                    {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, padding: 0, fontSize: 16, lineHeight: 1 }}>×</button>}
                </div>

                {/* Session Status Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiFilter size={13} color={C.sub} />
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: C.bg, color: C.text, cursor: 'pointer' }}>
                        <option value="">All Statuses</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="active">Live</option>
                        <option value="ended">Ended</option>
                    </select>
                </div>

                {/* Date Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiCalendar size={13} color={C.sub} />
                    <input
                        type="date"
                        value={filterDate}
                        onChange={e => setFilterDate(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: C.bg, color: C.text, cursor: 'pointer' }}
                    />
                    {filterDate && <button onClick={() => setFilterDate('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, fontSize: 16 }}>×</button>}
                </div>

                {/* Group By Toggle */}
                <div style={{ display: 'flex', gap: 4 }}>
                    {['invigilator', 'session', 'flat'].map(g => (
                        <button key={g} onClick={() => setGroupBy(g)} style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${groupBy === g ? C.accent : C.border}`, background: groupBy === g ? '#e0f2fe' : C.bg, color: groupBy === g ? C.accent : C.sub, fontWeight: groupBy === g ? 800 : 600, fontSize: 11, cursor: 'pointer', fontFamily: font, textTransform: 'capitalize' }}>
                            {g === 'flat' ? 'Table' : `By ${g.charAt(0).toUpperCase() + g.slice(1)}`}
                        </button>
                    ))}
                </div>

                <button onClick={load} style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                    <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
                </button>
            </div>

            {/* Main Content */}
            {loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontSize: 14 }}>Loading assignments...</div>
            ) : filtered.length === 0 ? (
                <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontSize: 14, background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}` }}>
                    No invigilator assignments found.
                </div>
            ) : groupBy === 'flat' ? (
                // ── Flat Table View ───────────────────────────────────────────
                <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: C.bg, borderBottom: `2px solid ${C.border}` }}>
                                    {['#', 'Invigilator', 'Hall', 'Subject', 'Department', 'Exam Date', 'Time', 'Students', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 800, color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((a, idx) => (
                                    <tr key={a.assignment_id} style={{ borderBottom: `1px solid ${C.border}`, background: a.session_status === 'active' ? '#f0fdf4' : 'transparent' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = a.session_status === 'active' ? '#f0fdf4' : 'transparent'}>
                                        <td style={{ padding: '11px 14px', color: C.sub, fontWeight: 600 }}>{idx + 1}</td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {a.invigilator_list?.length > 0 ? (
                                                <div>
                                                    {a.invigilator_list.map((inv, i) => (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i < a.invigilator_list.length - 1 ? 6 : 0 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                                                                {(inv.display_name || inv.name || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{inv.display_name || inv.name}</div>
                                                                {inv.designation && <div style={{ fontSize: 10, color: C.accent, fontWeight: 600 }}>{inv.designation}</div>}
                                                                {inv.department && <div style={{ fontSize: 10, color: C.sub }}>{inv.department}</div>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 12 }}>⚠ Unassigned</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            <div style={{ fontWeight: 800, color: C.text }}>{a.hall_number}</div>
                                            <div style={{ fontSize: 11, color: C.sub }}>{a.building}</div>
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            <div style={{ fontWeight: 700, color: '#4f46e5', fontSize: 12 }}>{a.subject_code}</div>
                                            <div style={{ fontSize: 12, color: C.text, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.subject_name}>{a.subject_name}</div>
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: 12, color: C.text }}>{a.department || '—'}</td>
                                        <td style={{ padding: '11px 14px', fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>{formatIndianDate(a.exam_date)}</td>
                                        <td style={{ padding: '11px 14px', fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>{a.exam_time || '—'}</td>
                                        <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                            <span style={{ padding: '3px 8px', borderRadius: 20, background: '#e0f2fe', color: C.accent, fontSize: 12, fontWeight: 700 }}>{a.students_count}/{a.capacity}</span>
                                        </td>
                                        <td style={{ padding: '11px 14px' }}><SessionBadge status={a.session_status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : groupBy === 'invigilator' ? (
                // ── Grouped by Invigilator ────────────────────────────────────
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {Object.values(groupedByInvig).sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '')).map(grp => {
                        const isOpen = expandedInvig === grp.display_name;
                        const liveCount = grp.rows.filter(r => r.session_status === 'active').length;
                        return (
                            <div key={grp.display_name} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${isOpen ? C.accent : C.border}`, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                                {/* Group Header */}
                                <div onClick={() => setExpandedInvig(isOpen ? null : grp.display_name)} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', background: isOpen ? '#e0f2fe' : C.bg }}>
                                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: grp.username === '__unassigned__' ? '#fef3c7' : 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, flexShrink: 0 }}>
                                        {grp.username === '__unassigned__' ? '⚠' : (grp.display_name?.charAt(0)?.toUpperCase() || '?')}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{grp.display_name}</div>
                                        <div style={{ fontSize: 12, color: C.sub, marginTop: 1 }}>
                                            {grp.designation && <span style={{ color: C.accent, fontWeight: 700, marginRight: 8 }}>{grp.designation}</span>}
                                            {grp.department && <span>{grp.department} · </span>}
                                            {grp.rows.length} hall assignment{grp.rows.length !== 1 ? 's' : ''}
                                            {liveCount > 0 && <span style={{ marginLeft: 8, background: '#d1fae5', color: C.green, padding: '1px 8px', borderRadius: 20, fontWeight: 800, fontSize: 10 }}>🔴 {liveCount} LIVE</span>}
                                        </div>
                                    </div>
                                    {isOpen ? <FiChevronUp color={C.sub} /> : <FiChevronDown color={C.sub} />}
                                </div>

                                {/* Expanded rows */}
                                {isOpen && (
                                    <div style={{ borderTop: `1px solid ${C.border}` }}>
                                        {grp.rows.map((a, i) => (
                                            <div key={a.assignment_id} style={{ padding: '14px 20px', borderBottom: i < grp.rows.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: a.session_status === 'active' ? '#f0fdf4' : 'transparent' }}>
                                                {/* Hall */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#e0f2fe', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiMapPin size={16} /></div>
                                                    <div>
                                                        <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{a.hall_number}</div>
                                                        <div style={{ fontSize: 11, color: C.sub }}>{a.building}</div>
                                                    </div>
                                                </div>
                                                {/* Subject */}
                                                <div style={{ flex: 1, minWidth: 140 }}>
                                                    <div style={{ fontWeight: 700, color: '#4f46e5', fontSize: 12 }}>{a.subject_code}</div>
                                                    <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{a.subject_name}</div>
                                                    <div style={{ fontSize: 11, color: C.sub }}>{a.department}{a.batch_name ? ` · ${a.batch_name}` : ''}</div>
                                                </div>
                                                {/* Date & Time */}
                                                <div style={{ minWidth: 120 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.text, fontWeight: 700 }}><FiCalendar size={12} />{formatIndianDate(a.exam_date)}</div>
                                                    {a.exam_time && <div style={{ fontSize: 11, color: C.sub, marginTop: 3 }}><FiClock size={11} style={{ verticalAlign: 'middle' }} /> {a.exam_time}</div>}
                                                </div>
                                                {/* Students */}
                                                <div style={{ textAlign: 'center', minWidth: 70 }}>
                                                    <div style={{ fontSize: 16, fontWeight: 900, color: C.text }}>{a.students_count}</div>
                                                    <div style={{ fontSize: 10, color: C.sub, fontWeight: 600 }}>/ {a.capacity} Seats</div>
                                                </div>
                                                {/* Status */}
                                                <SessionBadge status={a.session_status} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                // ── Grouped by Session ────────────────────────────────────────
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {Object.values(groupedBySession).map(grp => {
                        const isOpen = expandedInvig === grp.label;
                        const s = grp.session;
                        return (
                            <div key={grp.label} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${isOpen ? C.accent : C.border}`, overflow: 'hidden' }}>
                                <div onClick={() => setExpandedInvig(isOpen ? null : grp.label)} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', background: isOpen ? '#e0f2fe' : C.bg }}>
                                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#818cf8,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <FiBook size={20} color="#fff" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{s.subject_name} <span style={{ color: C.sub, fontWeight: 500, fontSize: 12 }}>({s.subject_code})</span></div>
                                        <div style={{ fontSize: 12, color: C.sub, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            <span><FiCalendar size={11} style={{ verticalAlign: 'middle' }} /> {formatIndianDate(s.exam_date)}</span>
                                            {s.exam_time && <span><FiClock size={11} style={{ verticalAlign: 'middle' }} /> {s.exam_time}</span>}
                                            <span>{s.department}</span>
                                            <span>{grp.rows.length} halls assigned</span>
                                        </div>
                                    </div>
                                    <SessionBadge status={s.session_status} />
                                    {isOpen ? <FiChevronUp color={C.sub} /> : <FiChevronDown color={C.sub} />}
                                </div>

                                {isOpen && (
                                    <div style={{ borderTop: `1px solid ${C.border}` }}>
                                        {grp.rows.map((a, i) => (
                                            <div key={a.assignment_id} style={{ padding: '14px 20px', borderBottom: i < grp.rows.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {/* Hall */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 100 }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#e0f2fe', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiMapPin size={16} /></div>
                                                    <div>
                                                        <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{a.hall_number}</div>
                                                        <div style={{ fontSize: 11, color: C.sub }}>{a.building} · {a.capacity} seats</div>
                                                    </div>
                                                </div>
                                                {/* Invigilator(s) */}
                                                <div style={{ flex: 1, minWidth: 160 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Invigilator(s)</div>
                                                    {a.invigilator_list?.length > 0 ? (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                            {a.invigilator_list.map((inv, j) => (
                                                                <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f9ff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '6px 12px' }}>
                                                                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                                                                        {(inv.display_name || inv.name || '?').charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{inv.display_name || inv.name}</div>
                                                                        {inv.designation && <div style={{ fontSize: 10, color: C.accent, fontWeight: 600 }}>{inv.designation}</div>}
                                                                        {inv.department && <div style={{ fontSize: 10, color: C.sub }}>{inv.department}</div>}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: C.orange, fontWeight: 700, fontSize: 13 }}>⚠ No invigilator assigned</span>
                                                    )}
                                                </div>
                                                {/* Students */}
                                                <div style={{ textAlign: 'center', minWidth: 70 }}>
                                                    <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{a.students_count}</div>
                                                    <div style={{ fontSize: 10, color: C.sub }}>/ {a.capacity} seats</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <style dangerouslySetInnerHTML={{ __html: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }} />
        </div>
    );
};

export default InvigilatorManage;
